import { Citation } from '../analytics/types';

export type QualityIssueType =
  | 'missing_values'
  | 'duplicate_rows'
  | 'type_conflicts'
  | 'invalid_dates'
  | 'category_noise';

export interface QualityIssue {
  id: string;
  type: QualityIssueType;
  column?: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  affectedRows: number;
  citation: Citation;
}

export interface CleaningOperation {
  id: string;
  type: 'fill_missing' | 'drop_duplicates' | 'normalize_dates' | 'trim_categories';
  description: string;
  column?: string;
}

export interface CleaningPreview {
  operations: CleaningOperation[];
  issues: QualityIssue[];
  beforeSample: any[];
  afterSample: any[];
  impactedRows: number;
}

export interface CleaningResult {
  cleanedRows: any[];
  auditTrail: Array<{
    operation: CleaningOperation;
    affectedRows: number;
    timestamp: string;
  }>;
}

const normalizeText = (value: unknown): string => String(value ?? '').trim().replace(/\s+/g, ' ');

export class DataQualityEngine {
  private rows: any[];
  private headers: string[];

  constructor(rows: any[], headers: string[]) {
    this.rows = rows;
    this.headers = headers;
  }

  detectIssues(): QualityIssue[] {
    const issues: QualityIssue[] = [];

    this.headers.forEach((header) => {
      const values = this.rows.map((row) => row[header]);
      const missingCount = values.filter((value) => value === null || value === undefined || value === '').length;

      if (missingCount > 0) {
        issues.push({
          id: `missing-${header}`,
          type: 'missing_values',
          column: header,
          description: `${missingCount} missing values in ${header}.`,
          severity: missingCount / Math.max(this.rows.length, 1) > 0.2 ? 'high' : 'medium',
          affectedRows: missingCount,
          citation: {
            id: `cite-missing-${header}`,
            type: 'data_quality',
            label: `${header} missingness`,
            detail: `${missingCount}/${this.rows.length} rows contain null or empty values.`
          }
        });
      }

      const trimmedChanges = values.filter((value) => value && normalizeText(value) !== String(value)).length;
      if (trimmedChanges > 0) {
        issues.push({
          id: `category-${header}`,
          type: 'category_noise',
          column: header,
          description: `${trimmedChanges} values in ${header} include extra spacing noise.`,
          severity: 'low',
          affectedRows: trimmedChanges,
          citation: {
            id: `cite-category-${header}`,
            type: 'data_quality',
            label: `${header} normalization`,
            detail: 'Whitespace normalization will align equivalent category labels.'
          }
        });
      }

      const invalidDateCount = values.filter((value) => {
        if (!value) return false;
        if (!String(value).includes('-') && !String(value).includes('/')) return false;
        const parsed = new Date(String(value));
        return !Number.isFinite(parsed.getTime());
      }).length;

      if (invalidDateCount > 0) {
        issues.push({
          id: `invalid-date-${header}`,
          type: 'invalid_dates',
          column: header,
          description: `${invalidDateCount} potentially invalid dates detected in ${header}.`,
          severity: 'medium',
          affectedRows: invalidDateCount,
          citation: {
            id: `cite-date-${header}`,
            type: 'data_quality',
            label: `${header} date validation`,
            detail: `${invalidDateCount} values fail Date parsing.`
          }
        });
      }
    });

    const duplicates = this.detectDuplicateRows();
    if (duplicates.length > 0) {
      issues.push({
        id: 'duplicate-rows',
        type: 'duplicate_rows',
        description: `${duplicates.length} duplicate rows detected.`,
        severity: duplicates.length / Math.max(this.rows.length, 1) > 0.05 ? 'high' : 'medium',
        affectedRows: duplicates.length,
        citation: {
          id: 'cite-duplicates',
          type: 'data_quality',
          label: 'Duplicate row scan',
          detail: 'Exact JSON row comparison.'
        }
      });
    }

    return issues;
  }

  buildPreview(): CleaningPreview {
    const issues = this.detectIssues();
    const operations: CleaningOperation[] = [];

    if (issues.some((issue) => issue.type === 'duplicate_rows')) {
      operations.push({
        id: 'op-drop-duplicates',
        type: 'drop_duplicates',
        description: 'Drop exact duplicate rows.'
      });
    }

    this.headers.forEach((header) => {
      if (issues.some((issue) => issue.type === 'missing_values' && issue.column === header)) {
        operations.push({
          id: `op-fill-${header}`,
          type: 'fill_missing',
          column: header,
          description: `Fill missing values in ${header} with "Unknown".`
        });
      }

      if (issues.some((issue) => issue.type === 'category_noise' && issue.column === header)) {
        operations.push({
          id: `op-trim-${header}`,
          type: 'trim_categories',
          column: header,
          description: `Trim and normalize whitespace in ${header}.`
        });
      }

      if (issues.some((issue) => issue.type === 'invalid_dates' && issue.column === header)) {
        operations.push({
          id: `op-date-${header}`,
          type: 'normalize_dates',
          column: header,
          description: `Normalize parseable values to ISO date in ${header}.`
        });
      }
    });

    const result = this.applyOperations(operations);

    return {
      operations,
      issues,
      beforeSample: this.rows.slice(0, 5),
      afterSample: result.cleanedRows.slice(0, 5),
      impactedRows: result.auditTrail.reduce((sum, entry) => sum + entry.affectedRows, 0)
    };
  }

  applyOperations(operations: CleaningOperation[]): CleaningResult {
    let workingRows = [...this.rows];
    const auditTrail: CleaningResult['auditTrail'] = [];

    operations.forEach((operation) => {
      switch (operation.type) {
        case 'drop_duplicates': {
          const seen = new Set<string>();
          const before = workingRows.length;
          workingRows = workingRows.filter((row) => {
            const key = JSON.stringify(row);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          auditTrail.push({
            operation,
            affectedRows: before - workingRows.length,
            timestamp: new Date().toISOString()
          });
          break;
        }
        case 'fill_missing': {
          if (!operation.column) break;
          let affected = 0;
          workingRows = workingRows.map((row) => {
            if (row[operation.column!] !== null && row[operation.column!] !== undefined && row[operation.column!] !== '') {
              return row;
            }
            affected += 1;
            return { ...row, [operation.column!]: 'Unknown' };
          });
          auditTrail.push({ operation, affectedRows: affected, timestamp: new Date().toISOString() });
          break;
        }
        case 'trim_categories': {
          if (!operation.column) break;
          let affected = 0;
          workingRows = workingRows.map((row) => {
            const currentValue = row[operation.column!];
            if (currentValue === null || currentValue === undefined) return row;
            const normalized = normalizeText(currentValue);
            if (normalized === String(currentValue)) return row;
            affected += 1;
            return { ...row, [operation.column!]: normalized };
          });
          auditTrail.push({ operation, affectedRows: affected, timestamp: new Date().toISOString() });
          break;
        }
        case 'normalize_dates': {
          if (!operation.column) break;
          let affected = 0;
          workingRows = workingRows.map((row) => {
            const currentValue = row[operation.column!];
            if (!currentValue) return row;
            const parsed = new Date(String(currentValue));
            if (!Number.isFinite(parsed.getTime())) return row;
            const nextValue = parsed.toISOString().split('T')[0];
            if (nextValue === currentValue) return row;
            affected += 1;
            return { ...row, [operation.column!]: nextValue };
          });
          auditTrail.push({ operation, affectedRows: affected, timestamp: new Date().toISOString() });
          break;
        }
        default:
          break;
      }
    });

    return { cleanedRows: workingRows, auditTrail };
  }

  private detectDuplicateRows(): any[] {
    const seen = new Set<string>();
    const duplicates: any[] = [];

    this.rows.forEach((row) => {
      const key = JSON.stringify(row);
      if (seen.has(key)) {
        duplicates.push(row);
        return;
      }
      seen.add(key);
    });

    return duplicates;
  }
}
